import { useState, useEffect } from 'react'
import {
  Shield, Users, MessageSquare, FileText, RefreshCw, Activity,
  MessageCircle, Trash2, Coins, ChevronLeft, ChevronRight,
  LayoutDashboard, Sparkles, LogOut, SlidersHorizontal,
} from 'lucide-react'
import { useAuth } from '../../components/auth/AuthProvider'
import { UsersManagement } from './components/UsersManagement'
import { AnalyticsDashboard } from './components/AnalyticsDashboard'
import { DeletedSessionsSection } from './components/DeletedSessionsSection'
import { TokenUsage } from './components/TokenUsage'
import { ReportsSection } from './components/ReportsSection'
import { ModelConfigurationSection } from './components/ModelConfigurationSection'
import { API_BASE_URL } from '../../lib/config'
import { useNavigate } from 'react-router-dom'
import './admin-shell.css'

interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalChats: number
  totalDeletedChats: number
  totalMessages: number
  totalDeletedMessages: number
  totalSuggestions: number
  todayUsers: number
  todayChats: number
  todayMessages: number
}

type AdminView = 'dashboard' | 'users' | 'token-usage' | 'reports' | 'deleted-sessions' | 'model-configuration'

const NAV_ITEMS: { id: AdminView; label: string; icon: React.ReactNode }[] = [
  { id: 'dashboard',        label: 'Dashboard',        icon: <LayoutDashboard size={17} /> },
  { id: 'users',            label: 'Users',             icon: <Users size={17} /> },
  { id: 'token-usage',      label: 'Token Usage',       icon: <Coins size={17} /> },
  { id: 'reports',          label: 'Reports',           icon: <FileText size={17} /> },
  { id: 'deleted-sessions', label: 'Deleted Sessions',  icon: <Trash2 size={17} /> },
  { id: 'model-configuration', label: 'Model Configuration', icon: <SlidersHorizontal size={17} /> },
]

export function AdminDashboardPage() {
  const { user, credentialUser, signOut } = useAuth()
  const navigate = useNavigate()

  const [view, setView] = useState<AdminView>('dashboard')
  const [collapsed, setCollapsed] = useState(false)
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0, activeUsers: 0, totalChats: 0, totalDeletedChats: 0,
    totalMessages: 0, totalDeletedMessages: 0, totalSuggestions: 0,
    todayUsers: 0, todayChats: 0, todayMessages: 0,
  })
  const [loading, setLoading] = useState(false)

  const displayName = user?.displayName ?? credentialUser?.username ?? user?.email ?? 'Admin'
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()

  useEffect(() => { fetchStats() }, [])

  const fetchStats = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}admin/stats`, { credentials: 'include' })
      if (res.ok) setStats(await res.json())
    } catch {
      void 0
    } finally { setLoading(false) }
  }

  const handleLogOut = async () => {
    try { await fetch(`${API_BASE_URL}logout`, { method: 'POST', credentials: 'include' }) } catch { void 0 }
    await signOut()
    navigate('/')
  }

  const STAT_CARDS = [
    { label: 'Total Users',     value: stats.totalUsers,      sub: `+${stats.todayUsers} today`,   icon: <Users size={15} />,       color: '#6366f1' },
    { label: 'Active Users',    value: stats.activeUsers,     sub: 'Last 7 days',                  icon: <Activity size={15} />,    color: '#8b5cf6' },
    { label: 'Total Chats',     value: stats.totalChats,      sub: `+${stats.todayChats} today`,   icon: <MessageSquare size={15} />, color: '#06b6d4' },
    { label: 'Total Messages',  value: stats.totalMessages,   sub: `+${stats.todayMessages} today`,icon: <MessageCircle size={15} />, color: '#10b981' },
    { label: 'Deleted Sessions',value: stats.totalDeletedChats, sub: 'Deleted by users',           icon: <Trash2 size={15} />,      color: '#f59e0b' },
    { label: 'Regenerated',     value: stats.totalDeletedMessages, sub: 'By users',                icon: <RefreshCw size={15} />,   color: '#ef4444' },
  ]

  const viewTitle: Record<AdminView, string> = {
    'dashboard': 'Dashboard',
    'users': 'Users',
    'token-usage': 'Token Usage',
    'reports': 'Reports',
    'deleted-sessions': 'Deleted Sessions',
    'model-configuration': 'Model Configuration',
  }

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--bg-canvas)', color: 'var(--text-primary)', fontFamily: 'system-ui, -apple-system, sans-serif', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside className={`adm-sidebar${collapsed ? ' collapsed' : ''}`}>
        {/* Header */}
        <div className="adm-sb-header">
          <div className="adm-logo-row">
            <div className="adm-logo-mark">
              <Sparkles size={15} color="#ffffff" />
            </div>
            <span className="adm-logo-text">AIRA</span>
            <span className="adm-badge">Admin</span>
          </div>
          {!collapsed && (
            <button className="adm-collapse-btn" onClick={() => setCollapsed(true)} title="Collapse">
              <ChevronLeft size={15} />
            </button>
          )}
        </div>

        {collapsed && (
          <div style={{ padding: '10px 0 0', display: 'flex', justifyContent: 'center' }}>
            <button className="adm-collapse-btn" style={{ display: 'flex' }} onClick={() => setCollapsed(false)} title="Expand">
              <ChevronRight size={15} />
            </button>
          </div>
        )}

        {/* Nav */}
        <nav className="adm-sb-nav">
          {!collapsed && <div className="adm-sb-section-title">Navigation</div>}
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              className={`adm-nav-item${view === item.id ? ' active' : ''}`}
              onClick={() => setView(item.id)}
              title={item.label}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="adm-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Bottom */}
        <div className="adm-sb-bottom">
          <button className="adm-bottom-btn" onClick={() => navigate('/chat')} title="Open Chat">
            <MessageCircle size={16} style={{ flexShrink: 0 }} />
            <span className="adm-bottom-label">Open Chat</span>
          </button>
          <button className="adm-bottom-btn" onClick={handleLogOut} title="Sign Out" style={{ color: 'var(--text-muted)' }}>
            <LogOut size={16} style={{ flexShrink: 0 }} />
            <span className="adm-bottom-label">Sign Out</span>
          </button>

          <div className="adm-profile-row">
            <div className="adm-avatar">{initials}</div>
            <div className="adm-profile-info">
              <div className="adm-profile-name">{displayName}</div>
              <div className="adm-profile-role">Administrator</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="adm-main">
        {/* Topbar */}
        <div className="adm-topbar">
          <div className="adm-topbar-title">
            <Shield size={16} style={{ color: 'var(--accent)' }} />
            {viewTitle[view]}
          </div>
          <div className="adm-topbar-actions">
            {view === 'dashboard' && (
            <button
              type="button"
              className="adm-icon-btn adm-btn-gradient"
              onClick={fetchStats}
              disabled={loading}
              title="Refresh stats"
            >
                <RefreshCw size={13} className={loading ? 'adm-refresh-spin' : undefined} />
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="adm-content">
          {view === 'dashboard' && (
            <>
              {/* Stat Cards */}
              <div className="adm-stats-grid">
                {STAT_CARDS.map(card => (
                  <div key={card.label} className="adm-stat-card">
                    <div className="adm-stat-top">
                      <span className="adm-stat-label">{card.label}</span>
                      <div className="adm-stat-icon" style={{ background: `${card.color}18`, color: card.color }}>
                        {card.icon}
                      </div>
                    </div>
                    <div className="adm-stat-value">{card.value.toLocaleString()}</div>
                    <div className="adm-stat-sub">{card.sub}</div>
                  </div>
                ))}
              </div>

              {/* Analytics */}
              <AnalyticsDashboard stats={stats} />
            </>
          )}

          {view === 'users' && <UsersManagement />}
          {view === 'token-usage' && <TokenUsage />}
          {view === 'reports' && <ReportsSection />}
          {view === 'deleted-sessions' && <DeletedSessionsSection />}
          {view === 'model-configuration' && <ModelConfigurationSection />}
        </div>
      </main>
    </div>
  )
}
